import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Avatar,
  LinearProgress,
  Tooltip,
  Divider,
  useTheme,
  useMediaQuery,
  Fade,
  Skeleton,
  Snackbar,
  Alert,
  Stack,
  CircularProgress,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  AttachMoney as MoneyIcon,
  ShoppingCart as CartIcon,
  Inventory as InventoryIcon,
  Person as PersonIcon,
  Restaurant as FoodIcon,
  CalendarToday as CalendarIcon,
  DateRange as RangeIcon,
  Today as TodayIcon,
  CalendarMonth as MonthIcon,
  Download as DownloadIcon,
  Print as PrintIcon,
  Refresh as RefreshIcon,
  LocalOffer as DiscountIcon,
  Groups as StaffIcon,
  ReceiptLong as ReceiptIcon,
  Warning as WarningIcon,
  CheckCircle as CheckIcon,
  AccessTime as TimeIcon,
} from '@mui/icons-material';
import { format, parseISO, startOfDay, endOfDay, startOfWeek, endOfWeek, startOfMonth, endOfMonth, subDays, subWeeks, subMonths, isWithinInterval } from 'date-fns';

const PERIODS = [
  { value: 'today', label: 'Today', labelUrdu: 'آج', icon: <TodayIcon /> },
  { value: 'yesterday', label: 'Yesterday', labelUrdu: 'کل', icon: <CalendarIcon /> },
  { value: 'week', label: 'This Week', labelUrdu: 'اس ہفتے', icon: <RangeIcon /> },
  { value: 'last_week', label: 'Last Week', labelUrdu: 'پچھلا ہفتہ', icon: <RangeIcon /> },
  { value: 'month', label: 'This Month', labelUrdu: 'اس مہینے', icon: <MonthIcon /> },
  { value: 'last_month', label: 'Last Month', labelUrdu: 'پچھلا مہینہ', icon: <MonthIcon /> },
  { value: 'custom', label: 'Custom', labelUrdu: 'کسٹم', icon: <CalendarIcon /> },
];

const Reports = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const [activeTab, setActiveTab] = useState(0);
  const [period, setPeriod] = useState('today');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [loading, setLoading] = useState(false);

  // Data states
  const [salesData, setSalesData] = useState([]);
  const [menuReport, setMenuReport] = useState([]);
  const [staffReport, setStaffReport] = useState([]);
  const [inventoryData, setInventoryData] = useState([]);
  const [orders, setOrders] = useState([]);
  const [categories, setCategories] = useState([]);

  // Summary stats
  const [stats, setStats] = useState({
    totalSales: 0,
    totalOrders: 0,
    avgOrderValue: 0,
    totalDiscount: 0,
    totalTax: 0,
    totalServiceCharge: 0,
    netProfit: 0,
    dineInSales: 0,
    takeawaySales: 0,
    deliverySales: 0,
    cashSales: 0,
    cardSales: 0,
    onlineSales: 0,
    cancelledOrders: 0,
    returnedAmount: 0,
  });

  const [topCustomers, setTopCustomers] = useState([]);
  const [hourlySales, setHourlySales] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // ==================== HELPERS ====================
  const showMessage = useCallback((message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  }, []);

  const formatPrice = useCallback((amount) => {
    const val = Number(amount || 0);
    return `PKR ${val.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }, []);

  const formatDate = useCallback((date) => {
    if (!date) return '-';
    try {
      return format(parseISO(date), 'dd MMM yyyy');
    } catch {
      return '-';
    }
  }, []);

  // ==================== SAFE API ACCESS ====================
  const getAPI = useCallback(() => {
    if (typeof window !== 'undefined' && window.electronAPI) {
      return window.electronAPI;
    }
    return null;
  }, []);

  const getStorage = useCallback(async () => {
    try {
      const module = await import('../database/storage');
      const storage = module.default || module;
      await storage.init();
      return storage;
    } catch (e) {
      console.warn('[Reports] Storage not available:', e);
      return null;
    }
  }, []);

  // ==================== DATE HELPERS ====================
  const getDateRange = useCallback((p) => {
    const now = new Date();
    switch (p) {
      case 'today': return { from: format(startOfDay(now), 'yyyy-MM-dd'), to: format(endOfDay(now), 'yyyy-MM-dd') };
      case 'yesterday': {
        const yest = subDays(now, 1);
        return { from: format(startOfDay(yest), 'yyyy-MM-dd'), to: format(endOfDay(yest), 'yyyy-MM-dd') };
      }
      case 'week': return { from: format(startOfWeek(now, { weekStartsOn: 1 }), 'yyyy-MM-dd'), to: format(endOfWeek(now, { weekStartsOn: 1 }), 'yyyy-MM-dd') };
      case 'last_week': {
        const lw = subWeeks(now, 1);
        return { from: format(startOfWeek(lw, { weekStartsOn: 1 }), 'yyyy-MM-dd'), to: format(endOfWeek(lw, { weekStartsOn: 1 }), 'yyyy-MM-dd') };
      }
      case 'month': return { from: format(startOfMonth(now), 'yyyy-MM-dd'), to: format(endOfMonth(now), 'yyyy-MM-dd') };
      case 'last_month': {
        const lm = subMonths(now, 1);
        return { from: format(startOfMonth(lm), 'yyyy-MM-dd'), to: format(endOfMonth(lm), 'yyyy-MM-dd') };
      }
      case 'custom': return { from: customFrom || format(startOfDay(now), 'yyyy-MM-dd'), to: customTo || format(endOfDay(now), 'yyyy-MM-dd') };
      default: return { from: format(startOfDay(now), 'yyyy-MM-dd'), to: format(endOfDay(now), 'yyyy-MM-dd') };
    }
  }, [customFrom, customTo]);

  // ==================== LOAD DATA ====================
  const loadAllData = useCallback(async () => {
    setLoading(true);
    try {
      const api = getAPI();
      const { from, to } = getDateRange(period);
      if (!from || !to) return;

      let sales, menu, staff, inv, allOrders, cats;

      if (!api) {
        // ✅ BROWSER MODE
        console.log('[Reports] Browser mode - using storage');
        const storage = await getStorage();
        if (!storage) throw new Error('Storage not available');

        [sales, menu, staff, inv, allOrders, cats] = await Promise.all([
          storage.getSalesReport ? storage.getSalesReport(from, to) : Promise.resolve([]),
          storage.getMenuReport ? storage.getMenuReport(from, to) : Promise.resolve([]),
          storage.getStaffReport ? storage.getStaffReport(from, to) : Promise.resolve([]),
          storage.getInventory ? storage.getInventory() : Promise.resolve([]),
          storage.getOrders ? storage.getOrders({}) : Promise.resolve([]),
          storage.getCategories ? storage.getCategories() : Promise.resolve([]),
        ]);
      } else {
        // ✅ ELECTRON MODE
        console.log('[Reports] Electron mode - using electronAPI');
        [sales, menu, staff, inv, allOrders, cats] = await Promise.all([
          api.getSalesReport(from, to),
          api.getMenuReport(from, to),
          api.getStaffReport(from, to),
          api.getInventory(),
          api.getOrders({}),
          api.getCategories(),
        ]);
      }

      // Filter orders by date range
      const filteredOrders = (allOrders || []).filter((o) => {
        try {
          const orderDate = format(parseISO(o.created_at), 'yyyy-MM-dd');
          return orderDate >= from && orderDate <= to;
        } catch {
          return false;
        }
      });

      setSalesData(sales || []);
      setMenuReport(menu || []);
      setStaffReport(staff || []);
      setInventoryData(inv || []);
      setOrders(filteredOrders);
      setCategories(cats || []);

      calculateStats(filteredOrders);
      calculateTopCustomers(filteredOrders);
      calculateHourlySales(filteredOrders);

    } catch (err) {
      console.error('[Reports] Load error:', err);
      showMessage('❌ Data load nahi ho saki!', 'error');
    } finally {
      setLoading(false);
    }
  }, [period, customFrom, customTo, getDateRange, getAPI, getStorage, showMessage]);

  // ==================== CALCULATE STATS ====================
  const calculateStats = useCallback((filteredOrders) => {
    const totalSales = filteredOrders.reduce((sum, o) => sum + (o.final_amount || 0), 0);
    const totalOrders = filteredOrders.length;
    const totalDiscount = filteredOrders.reduce((sum, o) => sum + (o.discount_amount || 0), 0);
    const totalTax = filteredOrders.reduce((sum, o) => sum + (o.tax_amount || 0), 0);
    const totalServiceCharge = filteredOrders.reduce((sum, o) => sum + (o.service_charge || 0), 0);
    const netProfit = totalSales - totalDiscount;

    const dineInSales = filteredOrders.filter((o) => o.order_type === 'dine_in').reduce((sum, o) => sum + (o.final_amount || 0), 0);
    const takeawaySales = filteredOrders.filter((o) => o.order_type === 'takeaway').reduce((sum, o) => sum + (o.final_amount || 0), 0);
    const deliverySales = filteredOrders.filter((o) => o.order_type === 'delivery').reduce((sum, o) => sum + (o.final_amount || 0), 0);

    const cashSales = filteredOrders.filter((o) => o.payment_method === 'cash').reduce((sum, o) => sum + (o.final_amount || 0), 0);
    const cardSales = filteredOrders.filter((o) => o.payment_method === 'card').reduce((sum, o) => sum + (o.final_amount || 0), 0);
    const onlineSales = filteredOrders.filter((o) => o.payment_method === 'online' || o.payment_method === 'jazzcash' || o.payment_method === 'easypaisa').reduce((sum, o) => sum + (o.final_amount || 0), 0);

    setStats({
      totalSales,
      totalOrders,
      avgOrderValue: totalOrders > 0 ? totalSales / totalOrders : 0,
      totalDiscount,
      totalTax,
      totalServiceCharge,
      netProfit,
      dineInSales,
      takeawaySales,
      deliverySales,
      cashSales,
      cardSales,
      onlineSales,
      cancelledOrders: filteredOrders.filter((o) => o.status === 'cancelled').length,
      returnedAmount: filteredOrders.filter((o) => o.status === 'cancelled').reduce((sum, o) => sum + (o.final_amount || 0), 0),
    });
  }, []);

  const calculateTopCustomers = useCallback((filteredOrders) => {
    const customerMap = {};
    filteredOrders.forEach((o) => {
      if (o.customer_name) {
        const key = o.customer_name + (o.customer_phone || '');
        if (!customerMap[key]) {
          customerMap[key] = { name: o.customer_name, phone: o.customer_phone, orders: 0, total: 0 };
        }
        customerMap[key].orders += 1;
        customerMap[key].total += (o.final_amount || 0);
      }
    });
    setTopCustomers(Object.values(customerMap).sort((a, b) => b.total - a.total).slice(0, 10));
  }, []);

  const calculateHourlySales = useCallback((filteredOrders) => {
    const hours = Array.from({ length: 24 }, (_, i) => ({ hour: i, count: 0, sales: 0 }));
    filteredOrders.forEach((o) => {
      try {
        const h = parseISO(o.created_at).getHours();
        hours[h].count += 1;
        hours[h].sales += (o.final_amount || 0);
      } catch {
        // ignore
      }
    });
    setHourlySales(hours);
  }, []);

  useEffect(() => {
    loadAllData();
  }, [loadAllData]);

  // ==================== HELPERS ====================
  const getPeakHour = useCallback(() => {
    if (hourlySales.length === 0) return '-';
    const peak = hourlySales.reduce((max, h) => h.sales > max.sales ? h : max, hourlySales[0]);
    return `${peak.hour}:00 - ${peak.hour + 1}:00 (${formatPrice(peak.sales)})`;
  }, [hourlySales, formatPrice]);

  // ==================== STAT CARD ====================
  const StatCard = ({ title, titleUrdu, value, icon, color, bg, trend }) => (
    <Grid item xs={6} sm={4} md={4} lg={2}>
      <Fade in timeout={300}>
        <Card sx={{ 
          borderRadius: 3, 
          borderLeft: `4px solid ${color}`, 
          bgcolor: bg, 
          height: '100%',
          transition: 'all 0.2s',
          '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 8px 24px rgba(0,0,0,0.1)' },
        }}>
          <CardContent sx={{ 
            p: { xs: 1.5, sm: 2 }, 
            '&:last-child': { pb: { xs: 1.5, sm: 2 } }
          }}>
            <Avatar sx={{ 
              bgcolor: color, 
              color: '#fff', 
              width: { xs: 32, sm: 40 }, 
              height: { xs: 32, sm: 40 }, 
              mb: 1 
            }}>
              {icon}
            </Avatar>
            <Typography variant="h6" sx={{ 
              fontWeight: 700, 
              color: color, 
              fontSize: { xs: '0.9rem', sm: '1.1rem' } 
            }}>
              {value}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
              {title}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: { xs: '0.55rem', sm: '0.65rem' }, display: 'block' }}>
              {titleUrdu}
            </Typography>
          </CardContent>
        </Card>
      </Fade>
    </Grid>
  );

  // ==================== SKELETON ====================
  const TableSkeleton = () => (
    <TableRow>
      <TableCell colSpan={7}>
        <Box sx={{ py: 2 }}>
          <Skeleton variant="text" width="100%" height={40} />
          <Skeleton variant="text" width="100%" height={40} />
          <Skeleton variant="text" width="100%" height={40} />
        </Box>
      </TableCell>
    </TableRow>
  );

  // ==================== RENDER SALES OVERVIEW ====================
  const renderSalesOverview = () => (
    <Box>
      {/* Summary Cards */}
      <Grid container spacing={1.5} sx={{ mb: 3 }}>
        <StatCard title="Total Sales" titleUrdu="کل فروخت" value={formatPrice(stats.totalSales)} icon={<MoneyIcon />} color="#2e7d32" bg="#e8f5e9" />
        <StatCard title="Total Orders" titleUrdu="کل آرڈرز" value={stats.totalOrders} icon={<CartIcon />} color="#1976d2" bg="#e3f2fd" />
        <StatCard title="Avg Order" titleUrdu="اوسط بل" value={formatPrice(stats.avgOrderValue)} icon={<ReceiptIcon />} color="#ed6c02" bg="#fff3e0" />
        <StatCard title="Net Profit" titleUrdu="خالص منافع" value={formatPrice(stats.netProfit)} icon={<TrendingUpIcon />} color="#9c27b0" bg="#f3e5f5" />
        <StatCard title="Discount" titleUrdu="رعایت" value={formatPrice(stats.totalDiscount)} icon={<DiscountIcon />} color="#d32f2f" bg="#ffebee" />
        <StatCard title="Tax" titleUrdu="ٹیکس" value={formatPrice(stats.totalTax)} icon={<CheckIcon />} color="#607d8b" bg="#eceff1" />
      </Grid>

      {/* Sales by Order Type & Payment Method */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: { xs: 2, sm: 2.5 }, borderRadius: 3, height: '100%' }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#1a1a2e', fontSize: { xs: '0.95rem', sm: '1.1rem' } }}>
              Sales by Order Type / آرڈر کی قسم
            </Typography>
            {[
              { label: 'Dine In', labelUrdu: 'بیٹھ کر', value: stats.dineInSales, color: '#4caf50' },
              { label: 'Takeaway', labelUrdu: 'پارسل', value: stats.takeawaySales, color: '#ff9800' },
              { label: 'Delivery', labelUrdu: 'ڈلیوری', value: stats.deliverySales, color: '#2196f3' },
            ].map((type) => {
              const percentage = stats.totalSales > 0 ? (type.value / stats.totalSales) * 100 : 0;
              return (
                <Box key={type.label} sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, fontSize: { xs: '0.8rem', sm: '0.85rem' } }}>
                      {type.label} ({type.labelUrdu})
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: type.color, fontSize: { xs: '0.8rem', sm: '0.85rem' } }}>
                      {formatPrice(type.value)}
                    </Typography>
                  </Box>
                  <LinearProgress variant="determinate" value={percentage} sx={{ height: 6, borderRadius: 3, bgcolor: '#f0f0f0', '& .MuiLinearProgress-bar': { bgcolor: type.color } }} />
                  <Typography variant="caption" sx={{ fontSize: { xs: '0.6rem', sm: '0.7rem' } }}>{percentage.toFixed(1)}%</Typography>
                </Box>
              );
            })}
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: { xs: 2, sm: 2.5 }, borderRadius: 3, height: '100%' }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#1a1a2e', fontSize: { xs: '0.95rem', sm: '1.1rem' } }}>
              Sales by Payment / ادائیگی کا طریقہ
            </Typography>
            {[
              { label: 'Cash', labelUrdu: 'نقد', value: stats.cashSales, color: '#4caf50' },
              { label: 'Card', labelUrdu: 'کارڈ', value: stats.cardSales, color: '#2196f3' },
              { label: 'Online', labelUrdu: 'آن لائن', value: stats.onlineSales, color: '#9c27b0' },
            ].map((method) => {
              const percentage = stats.totalSales > 0 ? (method.value / stats.totalSales) * 100 : 0;
              return (
                <Box key={method.label} sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, fontSize: { xs: '0.8rem', sm: '0.85rem' } }}>
                      {method.label} ({method.labelUrdu})
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: method.color, fontSize: { xs: '0.8rem', sm: '0.85rem' } }}>
                      {formatPrice(method.value)}
                    </Typography>
                  </Box>
                  <LinearProgress variant="determinate" value={percentage} sx={{ height: 6, borderRadius: 3, bgcolor: '#f0f0f0', '& .MuiLinearProgress-bar': { bgcolor: method.color } }} />
                  <Typography variant="caption" sx={{ fontSize: { xs: '0.6rem', sm: '0.7rem' } }}>{percentage.toFixed(1)}%</Typography>
                </Box>
              );
            })}
          </Paper>
        </Grid>
      </Grid>

      {/* Hourly Sales */}
      <Paper sx={{ p: { xs: 2, sm: 2.5 }, borderRadius: 3, mb: 3, overflowX: 'auto' }}>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#1a1a2e', fontSize: { xs: '0.95rem', sm: '1.1rem' } }}>
          Hourly Sales / گھنٹہ وار فروخت
          <Typography component="span" variant="caption" sx={{ color: 'text.secondary', ml: 1, fontSize: { xs: '0.6rem', sm: '0.7rem' } }}>
            Peak: {getPeakHour()}
          </Typography>
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 0.5, height: 120, minWidth: 300 }}>
          {hourlySales.map((h) => {
            const maxSales = Math.max(...hourlySales.map((x) => x.sales), 1);
            const height = (h.sales / maxSales) * 100;
            return (
              <Tooltip key={h.hour} title={`${h.hour}:00 - ${h.hour + 1}:00\nOrders: ${h.count}\nSales: ${formatPrice(h.sales)}`}>
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                  <Box
                    sx={{
                      width: '100%',
                      height: `${Math.max(height, 2)}%`,
                      bgcolor: height > 50 ? '#ff6b35' : height > 25 ? '#ff9800' : '#e0e0e0',
                      borderRadius: '2px 2px 0 0',
                      minHeight: 4,
                      transition: 'all 0.3s',
                    }}
                  />
                  <Typography variant="caption" sx={{ fontSize: { xs: '0.5rem', sm: '0.6rem' }, color: 'text.disabled' }}>
                    {h.hour}
                  </Typography>
                </Box>
              </Tooltip>
            );
          })}
        </Box>
      </Paper>

      {/* Daily Sales Table */}
      <Paper sx={{ borderRadius: 3, overflow: 'hidden' }}>
        <Typography variant="h6" sx={{ fontWeight: 700, p: 2, pb: 1, color: '#1a1a2e', fontSize: { xs: '0.95rem', sm: '1.1rem' } }}>
          Daily Sales Breakdown / روزانہ فروخت
        </Typography>
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#1a1a2e' }}>
                <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: { xs: '0.65rem', sm: '0.8rem' } }}>Date</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: { xs: '0.65rem', sm: '0.8rem' } }} align="center">Orders</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: { xs: '0.65rem', sm: '0.8rem' } }} align="right">Gross</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: { xs: '0.65rem', sm: '0.8rem' } }} align="right" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Discount</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: { xs: '0.65rem', sm: '0.8rem' } }} align="right" sx={{ display: { xs: 'none', md: 'table-cell' } }}>Tax</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: { xs: '0.65rem', sm: '0.8rem' } }} align="right" sx={{ display: { xs: 'none', lg: 'table-cell' } }}>Service</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: { xs: '0.65rem', sm: '0.8rem' } }} align="right">Net</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableSkeleton />
              ) : salesData.length > 0 ? (
                salesData.map((row, idx) => (
                  <TableRow key={idx} hover>
                    <TableCell sx={{ fontSize: { xs: '0.65rem', sm: '0.8rem' } }}>{formatDate(row.date)}</TableCell>
                    <TableCell align="center" sx={{ fontSize: { xs: '0.65rem', sm: '0.8rem' } }}>{row.total_orders}</TableCell>
                    <TableCell align="right" sx={{ fontSize: { xs: '0.65rem', sm: '0.8rem' } }}>{formatPrice(row.gross_sales)}</TableCell>
                    <TableCell align="right" sx={{ color: '#f44336', fontSize: { xs: '0.65rem', sm: '0.8rem' }, display: { xs: 'none', sm: 'table-cell' } }}>-{formatPrice(row.total_discount)}</TableCell>
                    <TableCell align="right" sx={{ fontSize: { xs: '0.65rem', sm: '0.8rem' }, display: { xs: 'none', md: 'table-cell' } }}>{formatPrice(row.total_tax)}</TableCell>
                    <TableCell align="right" sx={{ fontSize: { xs: '0.65rem', sm: '0.8rem' }, display: { xs: 'none', lg: 'table-cell' } }}>{formatPrice(row.total_service_charge)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, color: '#2e7d32', fontSize: { xs: '0.65rem', sm: '0.8rem' } }}>{formatPrice(row.net_sales)}</TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>No data available</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );

  // ==================== RENDER MENU REPORT ====================
  const renderMenuReport = () => (
    <Box>
      <Grid container spacing={1.5} sx={{ mb: 3 }}>
        <StatCard title="Items Sold" titleUrdu="فروخت اشیاء" value={menuReport.length} icon={<FoodIcon />} color="#4caf50" bg="#e8f5e9" />
        <StatCard title="Total Revenue" titleUrdu="کل آمدنی" value={formatPrice(menuReport.reduce((s, i) => s + i.total_revenue, 0))} icon={<MoneyIcon />} color="#ff9800" bg="#fff3e0" />
        <StatCard title="Avg Price" titleUrdu="اوسط قیمت" value={menuReport.length > 0 ? formatPrice(menuReport.reduce((s, i) => s + i.total_revenue, 0) / menuReport.reduce((s, i) => s + i.total_sold, 0)) : '0'} icon={<CheckIcon />} color="#2196f3" bg="#e3f2fd" />
      </Grid>

      <Paper sx={{ borderRadius: 3, overflow: 'hidden' }}>
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#1a1a2e' }}>
                <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: { xs: '0.65rem', sm: '0.8rem' } }}>#</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: { xs: '0.65rem', sm: '0.8rem' } }}>Item</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: { xs: '0.65rem', sm: '0.8rem' } }} align="center">Qty</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: { xs: '0.65rem', sm: '0.8rem' } }} align="right">Revenue</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: { xs: '0.65rem', sm: '0.8rem' } }} align="center">%</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: { xs: '0.65rem', sm: '0.8rem' } }}>Performance</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableSkeleton />
              ) : menuReport.length > 0 ? (
                menuReport.map((item, idx) => {
                  const totalRev = menuReport.reduce((s, i) => s + i.total_revenue, 0);
                  const percentage = totalRev > 0 ? (item.total_revenue / totalRev) * 100 : 0;
                  return (
                    <TableRow key={idx} hover>
                      <TableCell sx={{ fontWeight: 700, fontSize: { xs: '0.65rem', sm: '0.8rem' } }}>{idx + 1}</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: { xs: '0.65rem', sm: '0.8rem' } }}>{item.item_name}</TableCell>
                      <TableCell align="center">
                        <Chip size="small" label={item.total_sold} sx={{ bgcolor: '#e3f2fd', color: '#1976d2', fontWeight: 700, fontSize: { xs: '0.55rem', sm: '0.65rem' }, height: 22 }} />
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color: '#2e7d32', fontSize: { xs: '0.65rem', sm: '0.8rem' } }}>{formatPrice(item.total_revenue)}</TableCell>
                      <TableCell align="center" sx={{ fontSize: { xs: '0.65rem', sm: '0.8rem' } }}>{percentage.toFixed(1)}%</TableCell>
                      <TableCell>
                        <LinearProgress variant="determinate" value={Math.min(percentage * 5, 100)} sx={{ height: 6, borderRadius: 3, bgcolor: '#f0f0f0', '& .MuiLinearProgress-bar': { bgcolor: percentage > 20 ? '#4caf50' : percentage > 10 ? '#ff9800' : '#f44336' } }} />
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <FoodIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>No menu data available</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );

  // ==================== RENDER CUSTOMER REPORT ====================
  const renderCustomerReport = () => (
    <Box>
      <Paper sx={{ p: { xs: 2, sm: 2.5 }, borderRadius: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#1a1a2e', fontSize: { xs: '0.95rem', sm: '1.1rem' } }}>
          Top Customers / بہترین گاہک
        </Typography>
        <Grid container spacing={1.5}>
          {loading ? (
            Array.from({ length: 3 }).map((_, i) => (
              <Grid item xs={12} sm={6} md={4} key={i}>
                <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 3 }} />
              </Grid>
            ))
          ) : topCustomers.length > 0 ? (
            topCustomers.map((customer, idx) => (
              <Grid item xs={12} sm={6} md={4} key={idx}>
                <Card sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
                  <CardContent sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
                      <Avatar sx={{ bgcolor: idx < 3 ? '#ff6b35' : '#e0e0e0', color: idx < 3 ? '#fff' : '#666', fontWeight: 700, width: 36, height: 36 }}>
                        {idx + 1}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 700, fontSize: { xs: '0.8rem', sm: '0.85rem' } }}>{customer.name}</Typography>
                        {customer.phone && (
                          <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: { xs: '0.6rem', sm: '0.65rem' } }}>{customer.phone}</Typography>
                        )}
                      </Box>
                    </Box>
                    <Divider sx={{ my: 1 }} />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="caption" sx={{ fontSize: { xs: '0.6rem', sm: '0.65rem' } }}>{customer.orders} orders</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: '#2e7d32', fontSize: { xs: '0.8rem', sm: '0.85rem' } }}>{formatPrice(customer.total)}</Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))
          ) : (
            <Grid item xs={12}>
              <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center', py: 4 }}>
                No customer data available
              </Typography>
            </Grid>
          )}
        </Grid>
      </Paper>

      <Paper sx={{ borderRadius: 3, overflow: 'hidden' }}>
        <Typography variant="h6" sx={{ fontWeight: 700, p: 2, pb: 1, color: '#1a1a2e', fontSize: { xs: '0.95rem', sm: '1.1rem' } }}>
          Customer Orders / گاہکوں کے آرڈر
        </Typography>
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#1a1a2e' }}>
                <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: { xs: '0.65rem', sm: '0.8rem' } }}>Customer</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: { xs: '0.65rem', sm: '0.8rem' } }} sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Phone</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: { xs: '0.65rem', sm: '0.8rem' } }}>Type</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: { xs: '0.65rem', sm: '0.8rem' } }} align="center">Items</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: { xs: '0.65rem', sm: '0.8rem' } }} align="right">Amount</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: { xs: '0.65rem', sm: '0.8rem' } }}>Date</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableSkeleton />
              ) : orders.filter((o) => o.customer_name).length > 0 ? (
                orders.filter((o) => o.customer_name).map((order, idx) => (
                  <TableRow key={idx} hover>
                    <TableCell sx={{ fontWeight: 600, fontSize: { xs: '0.65rem', sm: '0.8rem' } }}>{order.customer_name}</TableCell>
                    <TableCell sx={{ fontSize: { xs: '0.65rem', sm: '0.8rem' }, display: { xs: 'none', sm: 'table-cell' } }}>{order.customer_phone || '-'}</TableCell>
                    <TableCell>
                      <Chip size="small" label={order.order_type?.replace('_', ' ')} sx={{ textTransform: 'capitalize', fontSize: { xs: '0.5rem', sm: '0.6rem' }, height: 20 }} />
                    </TableCell>
                    <TableCell align="center" sx={{ fontSize: { xs: '0.65rem', sm: '0.8rem' } }}>{order.items?.length || 0}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, color: '#2e7d32', fontSize: { xs: '0.65rem', sm: '0.8rem' } }}>{formatPrice(order.final_amount)}</TableCell>
                    <TableCell sx={{ fontSize: { xs: '0.65rem', sm: '0.8rem' } }}>{formatDate(order.created_at)}</TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <PersonIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>No customer orders found</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );

  // ==================== RENDER STAFF REPORT ====================
  const renderStaffReport = () => (
    <Box>
      <Grid container spacing={1.5} sx={{ mb: 3 }}>
        <StatCard title="Active Staff" titleUrdu="فعال عملہ" value={staffReport.length} icon={<StaffIcon />} color="#2196f3" bg="#e3f2fd" />
        <StatCard title="Staff Sales" titleUrdu="عملہ فروخت" value={formatPrice(staffReport.reduce((s, st) => s + (st.total_sales || 0), 0))} icon={<MoneyIcon />} color="#4caf50" bg="#e8f5e9" />
      </Grid>

      <Paper sx={{ borderRadius: 3, overflow: 'hidden' }}>
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#1a1a2e' }}>
                <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: { xs: '0.65rem', sm: '0.8rem' } }}>#</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: { xs: '0.65rem', sm: '0.8rem' } }}>Staff</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: { xs: '0.65rem', sm: '0.8rem' } }} align="center">Orders</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: { xs: '0.65rem', sm: '0.8rem' } }} align="right">Sales</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: { xs: '0.65rem', sm: '0.8rem' } }} align="right">Avg/Order</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: { xs: '0.65rem', sm: '0.8rem' } }}>Performance</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableSkeleton />
              ) : staffReport.length > 0 ? (
                staffReport.map((staff, idx) => {
                  const avg = staff.total_orders > 0 ? staff.total_sales / staff.total_orders : 0;
                  const maxSales = Math.max(...staffReport.map((s) => s.total_sales), 1);
                  const percentage = (staff.total_sales / maxSales) * 100;
                  return (
                    <TableRow key={idx} hover>
                      <TableCell sx={{ fontWeight: 700, fontSize: { xs: '0.65rem', sm: '0.8rem' } }}>{idx + 1}</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: { xs: '0.65rem', sm: '0.8rem' } }}>{staff.name}</TableCell>
                      <TableCell align="center">
                        <Chip size="small" label={staff.total_orders} sx={{ bgcolor: '#e3f2fd', color: '#1976d2', fontWeight: 700, fontSize: { xs: '0.55rem', sm: '0.65rem' }, height: 22 }} />
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color: '#2e7d32', fontSize: { xs: '0.65rem', sm: '0.8rem' } }}>{formatPrice(staff.total_sales)}</TableCell>
                      <TableCell align="right" sx={{ fontSize: { xs: '0.65rem', sm: '0.8rem' } }}>{formatPrice(avg)}</TableCell>
                      <TableCell>
                        <LinearProgress variant="determinate" value={percentage} sx={{ height: 6, borderRadius: 3, bgcolor: '#f0f0f0', '& .MuiLinearProgress-bar': { bgcolor: percentage > 75 ? '#4caf50' : percentage > 50 ? '#ff9800' : '#f44336' } }} />
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <StaffIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>No staff data available</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );

  // ==================== RENDER INVENTORY REPORT ====================
  const renderInventoryReport = () => {
    const lowStock = (inventoryData || []).filter((i) => Number(i.current_stock) <= Number(i.reorder_point));
    const stockValue = (inventoryData || []).reduce((s, i) => s + (Number(i.current_stock) * Number(i.cost_per_unit)), 0);

    return (
      <Box>
        <Grid container spacing={1.5} sx={{ mb: 3 }}>
          <StatCard title="Total Items" titleUrdu="کل اشیاء" value={inventoryData.length} icon={<InventoryIcon />} color="#2196f3" bg="#e3f2fd" />
          <StatCard title="Low Stock" titleUrdu="کم اسٹاک" value={lowStock.length} icon={<WarningIcon />} color="#d32f2f" bg="#ffebee" />
          <StatCard title="Stock Value" titleUrdu="اسٹاک قیمت" value={formatPrice(stockValue)} icon={<MoneyIcon />} color="#4caf50" bg="#e8f5e9" />
          <StatCard title="Avg Value" titleUrdu="اوسط قیمت" value={inventoryData.length > 0 ? formatPrice(stockValue / inventoryData.length) : '0'} icon={<CheckIcon />} color="#ff9800" bg="#fff3e0" />
        </Grid>

        {lowStock.length > 0 && (
          <Paper sx={{ borderRadius: 3, overflow: 'hidden', mb: 3, border: '2px solid #f44336' }}>
            <Box sx={{ p: 2, bgcolor: '#ffebee' }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#d32f2f', display: 'flex', alignItems: 'center', gap: 1, fontSize: { xs: '0.95rem', sm: '1.1rem' } }}>
                <WarningIcon /> Low Stock Alert / کم اسٹاک الرٹ
              </Typography>
            </Box>
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#ffcdd2' }}>
                    <TableCell sx={{ fontWeight: 700, fontSize: { xs: '0.65rem', sm: '0.8rem' } }}>Item</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: { xs: '0.65rem', sm: '0.8rem' } }}>Category</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: { xs: '0.65rem', sm: '0.8rem' } }} align="center">Current</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: { xs: '0.65rem', sm: '0.8rem' } }} align="center">Min</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: { xs: '0.65rem', sm: '0.8rem' } }} align="center">Reorder</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: { xs: '0.65rem', sm: '0.8rem' } }} align="right">Cost</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableSkeleton />
                  ) : (
                    lowStock.map((item) => (
                      <TableRow key={item.id} hover>
                        <TableCell sx={{ fontWeight: 600, fontSize: { xs: '0.65rem', sm: '0.8rem' } }}>{item.name}</TableCell>
                        <TableCell>
                          <Chip size="small" label={item.category} sx={{ textTransform: 'capitalize', fontSize: { xs: '0.5rem', sm: '0.6rem' }, height: 20 }} />
                        </TableCell>
                        <TableCell align="center">
                          <Chip size="small" label={`${item.current_stock} ${item.unit}`} color="error" sx={{ fontWeight: 700, fontSize: { xs: '0.55rem', sm: '0.65rem' }, height: 22 }} />
                        </TableCell>
                        <TableCell align="center" sx={{ fontSize: { xs: '0.65rem', sm: '0.8rem' } }}>{item.min_stock}</TableCell>
                        <TableCell align="center" sx={{ fontSize: { xs: '0.65rem', sm: '0.8rem' } }}>{item.reorder_point}</TableCell>
                        <TableCell align="right" sx={{ fontSize: { xs: '0.65rem', sm: '0.8rem' } }}>{formatPrice(item.cost_per_unit)}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}

        <Paper sx={{ borderRadius: 3, overflow: 'hidden' }}>
          <Typography variant="h6" sx={{ fontWeight: 700, p: 2, pb: 1, color: '#1a1a2e', fontSize: { xs: '0.95rem', sm: '1.1rem' } }}>
            Full Inventory / مکمل اسٹاک
          </Typography>
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#1a1a2e' }}>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: { xs: '0.65rem', sm: '0.8rem' } }}>Item</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: { xs: '0.65rem', sm: '0.8rem' } }}>Category</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: { xs: '0.65rem', sm: '0.8rem' } }} align="center">Stock</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: { xs: '0.65rem', sm: '0.8rem' } }} align="right">Cost/Unit</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: { xs: '0.65rem', sm: '0.8rem' } }} align="right">Total</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: { xs: '0.65rem', sm: '0.8rem' } }}>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableSkeleton />
                ) : inventoryData.length > 0 ? (
                  inventoryData.map((item) => {
                    const status = Number(item.current_stock) <= Number(item.min_stock) ? { label: 'Critical', color: '#d32f2f' } :
                      Number(item.current_stock) <= Number(item.reorder_point) ? { label: 'Low', color: '#ff9800' } :
                      { label: 'OK', color: '#4caf50' };
                    return (
                      <TableRow key={item.id} hover>
                        <TableCell sx={{ fontWeight: 600, fontSize: { xs: '0.65rem', sm: '0.8rem' } }}>{item.name}</TableCell>
                        <TableCell>
                          <Chip size="small" label={item.category} sx={{ textTransform: 'capitalize', fontSize: { xs: '0.5rem', sm: '0.6rem' }, height: 20 }} />
                        </TableCell>
                        <TableCell align="center" sx={{ fontSize: { xs: '0.65rem', sm: '0.8rem' } }}>{item.current_stock} {item.unit}</TableCell>
                        <TableCell align="right" sx={{ fontSize: { xs: '0.65rem', sm: '0.8rem' } }}>{formatPrice(item.cost_per_unit)}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600, fontSize: { xs: '0.65rem', sm: '0.8rem' } }}>{formatPrice(Number(item.current_stock) * Number(item.cost_per_unit))}</TableCell>
                        <TableCell>
                          <Chip size="small" label={status.label} sx={{ bgcolor: `${status.color}15`, color: status.color, fontWeight: 600, fontSize: { xs: '0.5rem', sm: '0.6rem' }, height: 20 }} />
                        </TableCell>
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                      <InventoryIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>No inventory data available</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>
    );
  };

  // ==================== MAIN RETURN ====================
  return (
    <Box sx={{ 
      px: { xs: 1, sm: 2, md: 3 },
      pb: { xs: 8, sm: 3 },
    }}>
      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: isMobile ? 'center' : 'right' }}
      >
        <Alert severity={snackbar.severity} sx={{ borderRadius: 2, fontWeight: 600 }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Header */}
      <Box sx={{ 
        mb: { xs: 2, sm: 3 }, 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between', 
        alignItems: { xs: 'flex-start', sm: 'center' }, 
        flexWrap: 'wrap', 
        gap: 2 
      }}>
        <Box>
          <Typography variant="h4" sx={{ 
            fontWeight: 700, 
            color: '#1a1a2e', 
            mb: 0.5,
            fontSize: { xs: '1.5rem', sm: '2rem', md: '2.25rem' }
          }}>
            Reports & Analytics
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
            رپورٹس | Sales, Inventory & Performance
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', width: { xs: '100%', sm: 'auto' } }}>
          <Button 
            variant="outlined" 
            startIcon={<DownloadIcon />} 
            sx={{ borderRadius: 2, fontSize: { xs: '0.7rem', sm: '0.875rem' }, px: { xs: 1.5, sm: 2 } }}
            size={isMobile ? 'small' : 'medium'}
          >
            {isMobile ? '' : 'Export CSV'}
          </Button>
          <Button 
            variant="outlined" 
            startIcon={<PrintIcon />} 
            sx={{ borderRadius: 2, fontSize: { xs: '0.7rem', sm: '0.875rem' }, px: { xs: 1.5, sm: 2 } }}
            size={isMobile ? 'small' : 'medium'}
          >
            {isMobile ? '' : 'Print'}
          </Button>
          <Button 
            variant="contained" 
            startIcon={<RefreshIcon />} 
            onClick={loadAllData} 
            disabled={loading}
            sx={{ bgcolor: '#ff6b35', '&:hover': { bgcolor: '#e55a2b' }, borderRadius: 2, fontSize: { xs: '0.7rem', sm: '0.875rem' }, px: { xs: 1.5, sm: 2 } }}
            size={isMobile ? 'small' : 'medium'}
          >
            {loading ? <CircularProgress size={20} /> : (isMobile ? '' : 'Refresh')}
          </Button>
        </Box>
      </Box>

      {/* Period Filter */}
      <Paper sx={{ 
        p: { xs: 1.5, sm: 2 }, 
        mb: 3, 
        borderRadius: 3, 
        display: 'flex', 
        gap: { xs: 1, sm: 2 }, 
        flexWrap: 'wrap', 
        alignItems: 'center',
        border: '1px solid',
        borderColor: 'divider',
      }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1a1a2e', fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
          Period / مدت:
        </Typography>
        <ToggleButtonGroup
          value={period}
          exclusive
          onChange={(e, val) => val && setPeriod(val)}
          size="small"
          sx={{
            '& .MuiToggleButton-root': {
              borderRadius: '20px !important',
              px: { xs: 1, sm: 2 },
              py: { xs: 0.3, sm: 0.5 },
              textTransform: 'none',
              mx: 0.3,
              fontSize: { xs: '0.6rem', sm: '0.8rem' },
            },
            '& .Mui-selected': {
              bgcolor: '#1a1a2e !important',
              color: '#fff !important',
            },
          }}
        >
          {PERIODS.map((p) => (
            <ToggleButton key={p.value} value={p.value}>
              {p.icon}
              <Box component="span" sx={{ ml: 0.3, display: { xs: 'none', sm: 'inline' } }}>{p.label}</Box>
            </ToggleButton>
          ))}
        </ToggleButtonGroup>

        {period === 'custom' && (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <TextField type="date" size="small" label="From" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ width: { xs: '100%', sm: 'auto' } }} />
            <TextField type="date" size="small" label="To" value={customTo} onChange={(e) => setCustomTo(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ width: { xs: '100%', sm: 'auto' } }} />
          </Box>
        )}
      </Paper>

      {/* Tabs */}
      <Paper sx={{ borderRadius: 3, mb: 2, overflow: 'auto' }}>
        <Tabs 
          value={activeTab} 
          onChange={(e, val) => setActiveTab(val)} 
          sx={{ px: { xs: 1, sm: 2 }, pt: 1 }}
          variant={isMobile ? 'scrollable' : 'standard'}
          scrollButtons={isMobile ? 'auto' : false}
          allowScrollButtonsMobile
        >
          <Tab 
            icon={<TrendingUpIcon />} 
            label={isMobile ? '' : 'Sales'} 
            sx={{ minWidth: { xs: 'auto', sm: 'auto' }, px: { xs: 1, sm: 2 }, fontSize: { xs: '0.65rem', sm: '0.8rem' } }}
          />
          <Tab 
            icon={<FoodIcon />} 
            label={isMobile ? '' : 'Menu'} 
            sx={{ minWidth: { xs: 'auto', sm: 'auto' }, px: { xs: 1, sm: 2 }, fontSize: { xs: '0.65rem', sm: '0.8rem' } }}
          />
          <Tab 
            icon={<PersonIcon />} 
            label={isMobile ? '' : 'Customers'} 
            sx={{ minWidth: { xs: 'auto', sm: 'auto' }, px: { xs: 1, sm: 2 }, fontSize: { xs: '0.65rem', sm: '0.8rem' } }}
          />
          <Tab 
            icon={<StaffIcon />} 
            label={isMobile ? '' : 'Staff'} 
            sx={{ minWidth: { xs: 'auto', sm: 'auto' }, px: { xs: 1, sm: 2 }, fontSize: { xs: '0.65rem', sm: '0.8rem' } }}
          />
          <Tab 
            icon={<InventoryIcon />} 
            label={isMobile ? '' : 'Inventory'} 
            sx={{ minWidth: { xs: 'auto', sm: 'auto' }, px: { xs: 1, sm: 2 }, fontSize: { xs: '0.65rem', sm: '0.8rem' } }}
          />
        </Tabs>
      </Paper>

      {/* Tab Content */}
      {loading && <LinearProgress sx={{ mb: 2, borderRadius: 1, height: 4 }} />}

      <Box sx={{ opacity: loading ? 0.6 : 1, transition: 'opacity 0.2s' }}>
        {activeTab === 0 && renderSalesOverview()}
        {activeTab === 1 && renderMenuReport()}
        {activeTab === 2 && renderCustomerReport()}
        {activeTab === 3 && renderStaffReport()}
        {activeTab === 4 && renderInventoryReport()}
      </Box>
    </Box>
  );
};

export default Reports;